import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, MessageCircle, ArrowLeft } from "lucide-react";
import ConsultationForm from "@/components/ConsultationForm";
import { CONTACT, whatsappLink } from "@/data/site";
import { Reveal } from "@/components/Reveal";

export default function Contact() {
  return (
    <section className="mx-auto max-w-7xl px-5 pb-24 pt-36 lg:px-8" data-testid="contact-page">
      <div className="flex flex-col items-center gap-4 pb-14 text-center">
        <span className="flex items-center gap-3 text-xs font-bold tracking-[0.25em] text-gold">
          <span className="h-px w-8 bg-gold/60" />تماس با ما<span className="h-px w-8 bg-gold/60" />
        </span>
        <h1 className="text-3xl font-black sm:text-4xl">گفت‌وگو را <span className="gold-text">همین امروز</span> شروع کنید</h1>
        <p className="max-w-xl text-sm leading-8 text-slate-400">جلسه اول مشاوره کاملاً رایگان است؛ تلفنی، واتساپ یا حضوری در دفاتر تهران، تورنتو و لس‌آنجلس.</p>
      </div>

      <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr]">
        <Reveal>
          <div className="glass-panel rounded-3xl border border-gold/25 p-7 sm:p-9">
            <h2 className="mb-2 font-display text-xl font-extrabold text-gold">فرم درخواست مشاوره رایگان</h2>
            <p className="mb-7 text-xs leading-6 text-slate-500">کارشناس ارشد در کمتر از ۲۴ ساعت کاری با شما تماس می‌گیرد.</p>
            <ConsultationForm />
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <a href={whatsappLink("سلام، برای مشاوره مهاجرت از وبسایت GM7 پیام می‌دهم.")} target="_blank" rel="noreferrer" className="card-glow flex items-center gap-3 rounded-2xl border border-[#25D366]/30 bg-[#25D366]/5 p-4" data-testid="contact-whatsapp-card">
              <MessageCircle size={22} className="text-[#25D366]" />
              <div><p className="text-sm font-bold text-slate-100">واتساپ مستقیم</p><p className="mt-0.5 text-xs text-slate-400" dir="ltr">{CONTACT.whatsappDisplay}</p></div>
            </a>
            <a href={`tel:${CONTACT.phonePrimaryIntl}`} className="card-glow flex items-center gap-3 rounded-2xl border border-gold/25 bg-gold/5 p-4" data-testid="contact-phone-card">
              <Phone size={20} className="text-gold" />
              <div><p className="text-sm font-bold text-slate-100">واتساپ و تماس</p><p className="mt-0.5 text-xs text-slate-400" dir="ltr">{CONTACT.phonePrimary}</p></div>
            </a>
            <a href={`tel:${CONTACT.phoneSecondaryIntl}`} className="card-glow flex items-center gap-3 rounded-2xl border border-gold/25 bg-navy-850 p-4" data-testid="contact-phone-card-2">
              <Phone size={20} className="text-gold" />
              <div><p className="text-sm font-bold text-slate-100">تماس مستقیم</p><p className="mt-0.5 text-xs text-slate-400" dir="ltr">{CONTACT.phoneSecondary}</p></div>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="card-glow flex items-center gap-3 rounded-2xl border border-gold/25 bg-navy-850 p-4" data-testid="contact-email-card">
              <Mail size={20} className="text-gold" />
              <div><p className="text-sm font-bold text-slate-100">ایمیل</p><p className="mt-0.5 text-xs text-slate-400" dir="ltr">{CONTACT.email}</p></div>
            </a>
          </div>
        </Reveal>

        <div className="space-y-6">
          <Reveal delay={0.1}>
            <div className="card-glow flex items-center gap-4 rounded-3xl border border-gold/25 bg-gradient-to-l from-navy-900 to-navy-850 p-6" data-testid="contact-support-24h-card">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gold/15 text-gold"><Clock size={22} /></span>
              <div>
                <h3 className="font-display text-base font-bold text-slate-50">{CONTACT.supportNote}</h3>
                <p className="mt-1.5 text-sm leading-7 text-slate-400">تماس، واتساپ یا پیام — در هر ساعت از شبانه‌روز پاسخ می‌گیرید.</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="card-glow rounded-3xl border border-gold/15 bg-navy-850 p-7" data-testid="contact-payment-card">
              <h3 className="font-display text-base font-bold text-gold">قرارداد و پرداخت — به دلار آمریکا</h3>
              <ul className="mt-4 space-y-3 text-sm leading-7 text-slate-300">
                <li className="flex items-start gap-3"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />تسویه‌حساب کامل بعد از رسیدن به مقصد</li>
                <li className="flex items-start gap-3"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />شرایط نقدی و اقساطی — اقساط در ۱۰ ماه، شروع از یک ماه پس از رسیدن شما</li>
                <li className="flex items-start gap-3"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />همه قراردادها برای همه کشورها به دلار آمریکا</li>
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="card-glow rounded-3xl border border-gold/15 bg-navy-850 p-7" data-testid="contact-phones-card">
              <h3 className="font-display text-base font-bold text-gold">شماره‌های تماس</h3>
              <div className="mt-4 space-y-3 text-sm text-slate-300">
                <p className="flex items-center justify-between gap-3"><span>واتساپ و تماس</span><span dir="ltr" className="font-bold text-gold">{CONTACT.phonePrimary}</span></p>
                <p className="flex items-center justify-between gap-3"><span>تماس مستقیم</span><span dir="ltr" className="font-bold text-gold">{CONTACT.phoneSecondary}</span></p>
              </div>
              <a href={whatsappLink("سلام، برای مشاوره مهاجرت از وبسایت GM7 پیام می‌دهم.")} target="_blank" rel="noreferrer" className="btn-ghost mt-5 w-full" data-testid="contact-whatsapp-cta">گفت‌وگوی فوری در واتساپ</a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
